import { toast } from 'react-toastify';
import { axiosCaller } from '../utils/apiCaller';
import { wrap } from './generalActions';
import { translatedText } from '../components/TranslatedText';

/**
 * Cascade deletion-preview + audit + restore actions for the tenant-side
 * project soft-delete workflow.
 *
 * Routes (all under auth:api):
 *   GET  projects/{id}/deletion-preview      → cascade counts + manifest, no delete
 *   GET  project-deletions[?status=]         → audit list, scoped to user's company
 *   GET  project-deletions/{id}              → single audit record
 *   POST project-deletions/{id}/restore      → reverse the cascade
 *
 * Delete itself stays on the existing DELETE projects/{id} route (see deleteProject
 * in actions/client.tsx) — these actions wrap the preview / list / restore endpoints.
 */

/**
 * Fetch the cascade preview for a project. Returns counts + IDs of every record that
 * forceDeleteProject WOULD soft-delete, without touching anything.
 *
 * Response shape:
 *   { data: { project_id, affected_counts: { [table]: number }, affected_records: { [table]: number[] } } }
 */
export const getProjectDeletionPreview = (projectId: number) => wrap(async (_dispatch: any) => {
    try {
        return await axiosCaller('GET', `projects/${projectId}/deletion-preview`);
    } catch (err) {
        toast.error(translatedText({ id: 'somethingWentWrongAlt', defaultMessage: 'Diqka shkoi gabim!' }));
    }
}, 'GET_PROJECT_DELETION_PREVIEW_ERROR');

/**
 * Paginated audit list of cascade deletions for the current company.
 *
 * Filters (all optional):
 *   status: 'active' | 'restored'   — defaults to both
 *   page: number                    — defaults to 1
 *   perPage: number                 — defaults to 25, capped at 100 by the backend
 */
export const getProjectDeletions = (filters: { status?: 'active' | 'restored'; page?: number; perPage?: number } = {}) =>
    wrap(async (_dispatch: any) => {
        try {
            const qs: string[] = [];
            if (filters.status) qs.push(`status=${filters.status}`);
            if (filters.page) qs.push(`page=${filters.page}`);
            if (filters.perPage) qs.push(`per_page=${filters.perPage}`);
            const suffix = qs.length ? `?${qs.join('&')}` : '';
            return await axiosCaller('GET', `project-deletions${suffix}`);
        } catch (err) {
            toast.error(translatedText({ id: 'somethingWentWrongAlt', defaultMessage: 'Diqka shkoi gabim!' }));
        }
    }, 'GET_PROJECT_DELETIONS_ERROR');

/**
 * Fetch a single deletion record (full manifest + status).
 */
export const getProjectDeletion = (recordId: number) => wrap(async (_dispatch: any) => {
    try {
        return await axiosCaller('GET', `project-deletions/${recordId}`);
    } catch (err) {
        toast.error(translatedText({ id: 'somethingWentWrongAlt', defaultMessage: 'Diqka shkoi gabim!' }));
    }
}, 'GET_PROJECT_DELETION_ERROR');

/**
 * Restore everything soft-deleted by the given cascade event.
 * Backend returns 409 if the record has already been restored.
 */
export const restoreProjectDeletion = (recordId: number) => wrap(async (_dispatch: any) => {
    try {
        return await axiosCaller('POST', `project-deletions/${recordId}/restore`);
    } catch (err) {
        toast.error(translatedText({ id: 'somethingWentWrongAlt', defaultMessage: 'Diqka shkoi gabim!' }));
    }
}, 'RESTORE_PROJECT_DELETION_ERROR');
